// Imported from the file rather than the barrel: the barrel also exports the
// locked page, and the paywall is reached before the app shell exists.
import { SuccessFeeTermsDialog } from '@/components/legal/success-fee-terms/SuccessFeeTermsDialog';
import { useSuccessFeeTermsQuery } from '@/hooks/queries/use-success-fee-terms';
import type React from 'react';
import { useTranslation } from 'react-i18next';

interface PlanTermsAcceptanceProps {
  /** A subscription was refused for want of an acceptance. */
  isOpen: boolean;
  onClose: () => void;
  onAccepted: () => void;
}

/**
 * The terms a merchant is asked to accept at the moment it commits to a plan.
 *
 * Deliberately inert until it is opened: the document is only read once the API
 * has refused a subscription for want of an acceptance, so an organization with
 * nothing to accept never asks for the terms and its pricing page makes exactly
 * the requests it made before this existed.
 *
 * A member with view access gets the same text and no action, which is the one
 * honest answer: it can reach this page and press a plan, and only an owner or
 * an administrator engages the company.
 */
export const PlanTermsAcceptance: React.FC<PlanTermsAcceptanceProps> = ({
  isOpen,
  onClose,
  onAccepted,
}) => {
  const { t } = useTranslation();
  const { data, isLoading } = useSuccessFeeTermsQuery({ enabled: isOpen });

  return (
    <SuccessFeeTermsDialog
      isOpen={isOpen}
      onClose={onClose}
      onAccepted={onAccepted}
      document={data?.document ?? null}
      canAccept={data?.state.canAccept ?? false}
      isLoading={isOpen && isLoading}
      helper={t('legal.successFeeTerms.subscribe.helper')}
    />
  );
};
